import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import firebaseApp from "../firebase/credenciales";
import { CONT_COLLECTIONS } from "./contabilidad/collections";

const db = getFirestore(firebaseApp);

// RUC de la empresa propietaria (Mi Boleta)
const RUC_PROPIETARIO = "20609212102";

/**
 * Convierte un valor a fecha de forma segura (misma lógica que ReporteDiasCalleCobros)
 */
const toDateSafe = (raw) => {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }

  if (raw && typeof raw.toDate === "function") {
    const tsDate = raw.toDate();
    return Number.isNaN(tsDate.getTime()) ? null : new Date(tsDate.getFullYear(), tsDate.getMonth(), tsDate.getDate());
  }

  if (typeof raw === "number") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  const v = String(raw || "").trim();
  if (!v) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const d = new Date(`${v}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Verifica si una fecha está vencida
 */
const isPastDue = (dueDate, baseDate = new Date()) => {
  if (!dueDate) return false;
  const hoy = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  return dueDate.getTime() < hoy.getTime();
};

/**
 * Calcula la diferencia en días entre dos fechas
 */
const diffDays = (fromDate, toDate = new Date()) => {
  if (!fromDate) return 0;
  const ms = toDate.getTime() - fromDate.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const diffDaysSigned = (fromDate, toDate) => {
  if (!fromDate || !toDate) return 0;
  return Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Suma días calendario manteniendo solo fecha (sin hora)
 */
const addCalendarDays = (baseDate, days) => {
  if (!baseDate) return null;
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Suma días hábiles lunes a sábado (excluye domingo)
 */
const addBusinessDaysMonToSat = (baseDate, days) => {
  if (!baseDate) return null;
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  let counted = 0;
  while (counted < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day >= 1 && day <= 6) counted += 1;
  }
  return d;
};

const toIsoDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Obtiene las facturas pendientes de pago de un cliente específico
 * que fueron emitidas por la empresa propietaria (Mi Boleta)
 * Usa la misma lógica que ReporteDiasCalleCobros.js
 * 
 * @param {string} rucCliente - RUC del cliente a consultar
 * @param {number} diasVencimiento - Días mínimos de vencimiento para considerar la deuda (default: 45, usado solo si no hay fecha_corte)
 * @param {number} diasAvisoCorte - Días antes de la fecha_corte para mostrar aviso (default: 5)
 * @returns {Promise<{facturas: Array, totalDeuda: number, totalesPorMoneda: Object, tieneDeudaVencida: boolean, fechaLimitePagoIso: string | null, plazoPagoVencido: boolean, bloquearContinuar: boolean, fechaCorteBloqueoIso: string | null, puedeCerrarModalPorPago: boolean, enPeriodoAviso: boolean, diasHastaCorteMinimo: number | null}>}
 */
export const obtenerDeudaPendienteCliente = async (rucCliente, diasVencimiento = 45, diasAvisoCorte = 5) => {
  const emptyResult = {
    facturas: [],
    totalDeuda: 0,
    totalesPorMoneda: {},
    tieneDeudaVencida: false,
    fechaLimitePagoIso: null,
    plazoPagoVencido: false,
    bloquearContinuar: false,
    fechaCorteBloqueoIso: null,
    puedeCerrarModalPorPago: false,
    enPeriodoAviso: false,
    diasHastaCorteMinimo: null,
  };

  if (!rucCliente || rucCliente === RUC_PROPIETARIO) {
    return emptyResult;
  }

  try {
    // Buscar TODOS los comprobantes de venta de Mi Boleta con estado PENDIENTE
    // y luego filtrar por cliente en memoria (para evitar problemas de índices)
    const q = query(
      collection(db, CONT_COLLECTIONS.COMPROBANTES_VENTA),
      where("ruc_empresa", "==", RUC_PROPIETARIO),
      where("estado_pago", "==", "PENDIENTE")
    );

    const snap = await getDocs(q);
    const hoy = new Date();
    const hoySinHora = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

    const comprobantesCliente = snap.docs.filter((d) => d.data().cliente_ruc === rucCliente);
    const puedeCerrarModalPorPago = comprobantesCliente.some((d) => {
      const data = d.data();
      return Boolean(toDateSafe(data.fecha_pago || data.fechaPago || null));
    });

    // Filtrar por cliente_ruc en memoria y obtener todas las facturas pendientes sin pago ni anulación por NC
    const todasLasFacturas = comprobantesCliente
      .filter((d) => {
        const data = d.data();
        const fechaPago = toDateSafe(data.fecha_pago || data.fechaPago || null);
        const estadoPago = String(data.estado_pago || "").trim().toUpperCase();
        const estadoComp = String(data.estado || "").trim().toUpperCase();
        const esAnuladoNC =
          estadoPago === "ANULADO" ||
          estadoPago === "CANCELADO_NC" ||
          estadoPago === "NC_APLICADA" ||
          estadoComp === "ANULADO" ||
          data.anulado === true ||
          data.tiene_nc === true ||
          Boolean(data.nota_credito_id);

        return !fechaPago && !esAnuladoNC;
      })
      .map((d) => {
        const data = d.data();
        // Misma lógica de fechas que ReporteDiasCalleCobros.js
        const fechaEmision = String(data.fecha_emision || "");
        const fechaVencimiento = String(data.fecha_vencimiento || data.fecha_vcto || data.fecha_emision || "");
        const dEmision = toDateSafe(fechaEmision);
        const dVenc = toDateSafe(fechaVencimiento);
        const dFechaCorte = toDateSafe(data.fecha_corte || data.fechaCorte || null);
        const tieneFechaCorte = dFechaCorte !== null;
        const diasEnCalle = diffDays(dEmision, hoy);
        const diasVencido = isPastDue(dVenc, hoy) ? diffDays(dVenc, hoy) : 0;
        const diasHastaCorte = tieneFechaCorte ? diffDaysSigned(hoySinHora, dFechaCorte) : null;
        const esCritica = tieneFechaCorte
          ? diasHastaCorte <= diasAvisoCorte
          : diasVencido >= diasVencimiento;
        const fechaInicioAviso = (!tieneFechaCorte && diasVencido >= diasVencimiento) ? addCalendarDays(dVenc, diasVencimiento) : null;
        const fechaLimitePago = fechaInicioAviso ? addBusinessDaysMonToSat(fechaInicioAviso, 5) : null;

        return {
          id: d.id,
          tipo: data.tipo || "FACTURA",
          serie: data.serie || "",
          numero: data.numero || "",
          fechaEmision,
          fechaVencimiento,
          diasEnCalle,
          diasVencido,
          total: Number(data.total) || 0,
          moneda: data.moneda || "PEN",
          estaVencida: diasVencido > 0,
          tieneFechaCorte,
          diasHastaCorte,
          esCritica,
          fechaInicioAvisoIso: toIsoDate(fechaInicioAviso),
          fechaLimitePagoIso: toIsoDate(fechaLimitePago),
          fechaCorteIso: toIsoDate(dFechaCorte),
        };
      })
      .sort((a, b) => b.diasVencido - a.diasVencido);

    // Nueva lógica: usar fecha_corte como controlador principal
    const facturasCriticas = todasLasFacturas.filter((f) => f.esCritica);
    const facturasSinCorteCriticas = facturasCriticas.filter((f) => !f.tieneFechaCorte);
    const facturasConCorteCriticas = facturasCriticas.filter((f) => f.tieneFechaCorte);

    // Si tiene al menos una crítica, devolver TODAS las facturas pendientes
    const tieneFacturaCritica = facturasCriticas.length > 0;
    const facturasMostrar = tieneFacturaCritica ? todasLasFacturas : [];
    const totalDeuda = facturasMostrar.reduce((acc, f) => acc + f.total, 0);
    const totalesPorMoneda = facturasMostrar.reduce((acc, f) => {
      const key = String(f.moneda || "PEN").toUpperCase();
      acc[key] = (acc[key] || 0) + (Number(f.total) || 0);
      return acc;
    }, {});

    // Bloqueo por fecha_corte (principal)
    const bloquearPorCorte = facturasConCorteCriticas.some((f) => f.diasHastaCorte <= 0);

    // Bloqueo por vencimiento sin corte (fallback)
    const fechasLimiteCriticas = facturasSinCorteCriticas
      .map((f) => toDateSafe(f.fechaLimitePagoIso))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
    const fechaLimiteMasAntigua = fechasLimiteCriticas.length > 0 ? fechasLimiteCriticas[0] : null;
    const plazoPagoVencido = fechaLimiteMasAntigua
      ? fechaLimiteMasAntigua.getTime() < hoySinHora.getTime()
      : false;

    const bloquearContinuar = bloquearPorCorte || (!bloquearPorCorte && plazoPagoVencido);

    // Periodo de aviso (5 días antes del corte)
    const enPeriodoAviso = facturasConCorteCriticas.some(
      (f) => f.diasHastaCorte > 0 && f.diasHastaCorte <= diasAvisoCorte
    );

    // Días hasta el corte más próximo
    const diasHastaCorteValores = facturasConCorteCriticas
      .filter((f) => f.diasHastaCorte !== null)
      .map((f) => f.diasHastaCorte);
    const diasHastaCorteMinimo = diasHastaCorteValores.length > 0
      ? Math.min(...diasHastaCorteValores)
      : null;

    // Fecha de corte más próxima (para mostrar en UI)
    const fechasCorte = facturasConCorteCriticas
      .map((f) => toDateSafe(f.fechaCorteIso))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
    const fechaInicioBloqueo = fechasCorte.length > 0 ? fechasCorte[0] : null;

    return {
      facturas: facturasMostrar,
      totalDeuda,
      totalesPorMoneda,
      tieneDeudaVencida: tieneFacturaCritica,
      fechaLimitePagoIso: toIsoDate(fechaLimiteMasAntigua),
      plazoPagoVencido,
      bloquearContinuar,
      fechaCorteBloqueoIso: toIsoDate(fechaInicioBloqueo),
      puedeCerrarModalPorPago,
      enPeriodoAviso,
      diasHastaCorteMinimo,
    };
  } catch (error) {
    console.error("Error al obtener deuda pendiente del cliente:", error);
    return emptyResult;
  }
};

/**
 * Verifica si el usuario actual es el propietario del sistema
 * @param {string} rucUsuario - RUC del usuario
 * @returns {boolean}
 */
export const esUsuarioPropietario = (rucUsuario) => {
  return rucUsuario === RUC_PROPIETARIO;
};

export { RUC_PROPIETARIO };
